/**
 * /admin/companies/:id — System Admin Company detail (J-A20).
 *
 * Shows Summary, Members link, Assigned Tests section + AssignmentPicker
 * modal. Closes PRD Story 95 mapping gap. The picker is composed from
 * `@/components/ui` primitives only (Inv 15).
 */

import { useEffect, useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { adminApi, type CompanyAssignmentsPayload } from '../../../state/admin/api.ts';
import { AdminLayout } from '../layout.tsx';
import { AssignmentPicker } from '../../../components/admin/AssignmentPicker.tsx';
import { Badge, Button, Card, Heading, Link } from '@/components/ui';

export function AdminCompanyDetailPage(): ReactElement {
  const params = useParams<{ id: string }>();
  const companyId = params.id ?? '';

  const [payload, setPayload] = useState<CompanyAssignmentsPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [pickerOpen, setPickerOpen] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const reload = (): void => {
    void (async () => {
      setLoading(true);
      setError(null);
      const env = await adminApi.listCompanyAssignments(companyId, {
        include_status: 'active',
      });
      if (!env.ok) {
        setError(`${env.error.code}: ${env.error.message}`);
        setLoading(false);
        return;
      }
      setPayload(env.data);
      setLoading(false);
    })();
  };

  useEffect(() => {
    if (companyId === '') return;
    reload();
    // reload is stable closure over companyId; deliberate mount-driven effect.
    // eslint-disable-next-line
  }, [companyId]);

  const handleSave = (changes: { assign: string[]; unassign: string[] }): void => {
    void (async () => {
      setSaving(true);
      setError(null);
      const env = await adminApi.saveCompanyAssignments(companyId, changes);
      if (!env.ok) {
        setError(`${env.error.code}: ${env.error.message}`);
        setSaving(false);
        return;
      }
      setToast(
        `Assignments updated — ${String(env.data.assigned)} assigned, ${String(env.data.unassigned)} unassigned.`,
      );
      setSaving(false);
      setPickerOpen(false);
      reload();
    })();
  };

  const assigned = payload?.items.filter((i) => i.assigned) ?? [];

  return (
    <AdminLayout title={payload?.company.name ?? 'Company'} eyebrow="Admin · Company">
      <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
        <Link
          to="/admin/companies"
          className="font-sans text-small text-ink-2 hover:text-accent"
        >
          ← Back to Companies
        </Link>
      </div>

      {error !== null ? (
        <p role="alert" className="mt-4 font-sans text-small text-error">
          {error}
        </p>
      ) : null}

      {toast !== null ? (
        <p role="status" className="mt-4 font-sans text-small text-ink-2">
          {toast}
        </p>
      ) : null}

      <section aria-labelledby="company-summary-heading" className="mt-6">
        <Heading
          level="h2"
          id="company-summary-heading"
          className="text-h3 text-ink"
        >
          Summary
        </Heading>
        {loading ? (
          <p className="mt-2 font-sans text-body text-muted">Loading…</p>
        ) : payload === null ? (
          <p className="mt-2 font-sans text-body text-muted">Company not loaded.</p>
        ) : (
          <Card variant="default" className="mt-3 p-4">
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted">
                  Catalogue
                </dt>
                <dd className="mt-1 font-mono text-body text-ink">
                  {String(payload.catalogue_count)} tests
                </dd>
              </div>
              <div>
                <dt className="font-mono text-eyebrow uppercase tracking-wide text-muted">
                  Assigned
                </dt>
                <dd className="mt-1 font-mono text-body text-ink">
                  {String(payload.assigned_count)} tests
                </dd>
              </div>
            </dl>
          </Card>
        )}
      </section>

      <section aria-labelledby="assigned-tests-heading" className="mt-8">
        <div className="flex items-center justify-between">
          <Heading
            level="h2"
            id="assigned-tests-heading"
            className="text-h3 text-ink"
          >
            Assigned Tests
          </Heading>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              setPickerOpen(true);
            }}
            disabled={loading || payload === null}
          >
            Assign Tests
          </Button>
        </div>

        {assigned.length === 0 && !loading ? (
          <Card variant="default" className="mt-3 p-6 text-center">
            <p className="font-sans text-body text-muted">
              No tests assigned to this company yet.
            </p>
          </Card>
        ) : (
          <Card variant="default" className="mt-3 overflow-hidden p-0">
            <table className="w-full border-collapse text-small">
              <thead>
                <tr className="bg-bg-warm">
                  <th
                    scope="col"
                    className="border-b border-line px-4 py-2 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    Test
                  </th>
                  <th
                    scope="col"
                    className="border-b border-line px-4 py-2 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    Track / Level
                  </th>
                  <th
                    scope="col"
                    className="border-b border-line px-4 py-2 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {assigned.map((t) => (
                  <tr key={t.id} className="border-b border-line last:border-b-0">
                    <td className="px-4 py-2 font-sans text-body text-ink">
                      {t.title}
                    </td>
                    <td className="px-4 py-2 font-mono text-mono text-muted">
                      {(t.track ?? '—') + ' / ' + (t.level ?? '—')}
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant="success">{t.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </section>

      <AssignmentPicker
        open={pickerOpen}
        payload={payload}
        loading={loading}
        saving={saving}
        error={error}
        onClose={() => {
          setPickerOpen(false);
        }}
        onSave={handleSave}
      />
    </AdminLayout>
  );
}
